/**
 * AI Edits Adapter for Agentic Chat Micro Frontend
 *
 * This adapter connects the agentic-chat microfrontend to content-studio's AI Edits backend.
 * It implements the AgenticChatAdapter interface and handles:
 * - Message sending and receiving
 * - Agent status polling
 * - Chat history loading
 * - Recipes, help actions, and suggestions
 * - Edit application via processAIEditsResponse
 *
 * @see apps/agentic-chat/src/adapters/README.md for adapter implementation guide
 */

import {
  assetsAiEditsGetAiEditsCreate,
  assetsAiEditsGetChatHistoryCreate,
  assetsAiEditsSendUserMessageCreate,
  assetsAiEditsUpdateAiEditsStatusCreate,
  assetsAiEditsClearChatHistoryCreate
} from '@goldcast/api/content';

import JSConfetti from 'js-confetti';
import type { Channel } from 'pusher-js';
import type {
  AgenticChatAdapter,
  Recipe,
  RecipeData,
  HelpAction,
  SuggestionCategory,
  ChatResponse,
  ChatMessage,
  AgenticContext,
  AgentStatus,
  StreamingUpdate,
  StreamingUpdateCallback
} from 'agentic_chat/types';
import { AGENT_STATES } from './types';
import { handleLinkedInOptimization, handleYouTubeOptimization } from './utils/quickActionHandler';
import { CACHE_KEYS, getFromCache, setInCache, clearCache } from './cache';
import type { AIAgentStreamingResponse } from './types';
import { getHelpActionsByContext, getSuggestionsByContext } from './data/AIAgentActionsData';
import { processAIEditsResponse } from './utils/aiEditsProcessor';
import { mapChatMessage, mapSuggestionCategory, mapAgentState } from './constants';
import { SubscriptionManager } from './core/subscriptionManager';
import { acquireLock, releaseLock } from './core/processingLocks';
import {
  prefetchModuleAndStyles,
  getCachedCssContent,
  clearCachedCss,
  resetModulePrefetch
} from './core/modulePrefetch';
import {
  generateRecipe as generateRecipeService,
  updateRecipe as updateRecipeService,
  deleteRecipe as deleteRecipeService,
  getRecipeTags as getRecipeTagsService,
  getRecipes as getRecipesService,
  getRecommendedRecipe as getRecommendedRecipeService
} from './core/recipeService';
import { currentClip } from '@/stores/clip';
import { showSuccessToast, showErrorToast } from '@/libs/toast/toast';
import { aiEditsStore } from '@/stores/aiEditsStore';

let confettiInstance: JSConfetti | null = null;

function triggerConfetti(): void {
  requestAnimationFrame(() => {
    if (!confettiInstance) {
      confettiInstance = new JSConfetti();
    }
    confettiInstance.addConfetti();
    setTimeout(() => confettiInstance?.clearCanvas(), 1000);
  });
}

/**
 * AI Edits Adapter implementation
 *
 * Connects the agentic-chat microfrontend to content-studio's AI Edits backend.
 *
 * Features singleton subscription management to prevent duplicate Pusher handlers
 * across component mount/unmount cycles.
 *
 * @example
 * ```tsx
 * const adapter = new AIEditsAdapter(orgChannel);
 *
 * const context: AgenticContext = {
 *   type: 'AI_EDITS',
 *   features: {
 *     recipes: true,
 *     helpActions: true,
 *     suggestions: true,
 *     streaming: true
 *   },
 *   metadata: {
 *     clipId: 'clip-123'
 *   }
 * };
 *
 * <AgenticChat adapter={adapter} context={context} />
 * ```
 */
export class AIEditsAdapter implements AgenticChatAdapter {
  private pusherChannel: Channel | null;
  private subscriptionManager = SubscriptionManager.getInstance();
  private currentClipId: string | null = null;

  static getCachedCssContent(): string | null {
    return getCachedCssContent();
  }

  static async prefetchModuleAndStyles(): Promise<void> {
    return prefetchModuleAndStyles();
  }

  static clearCachedCss(): void {
    clearCachedCss();
  }

  static resetModulePrefetch(): void {
    resetModulePrefetch();
  }

  // ==================== Instance Methods ====================

  constructor(pusherChannel?: Channel | null) {
    this.pusherChannel = pusherChannel || null;
  }

  clearCache(): void {
    clearCache();
  }

  /**
   * Prefetch all data for the sidebar (recipes, status, history, recommended recipe).
   * Populates cache for instant rendering when sidebar opens.
   * Updates store with agentStatus.
   *
   * @param context - The current context (must contain clipId in metadata)
   */
  async prefetchData(context: AgenticContext): Promise<void> {
    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      window?.logger?.warn('AIEditsAdapter: Cannot prefetch data without clipId');
      return;
    }

    this.currentClipId = clipId;

    try {
      // Run getStatus FIRST to get and cache editId
      await this.getStatus(context).catch(() => {});

      // Then run others in parallel - getChatHistory will use cached editId
      await Promise.all([
        this.getRecipes(context).catch(() => {}),
        this.getRecommendedRecipe(context).catch(() => {}),
        this.getChatHistory(context).catch(() => {})
      ]);
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error prefetching data:', error);
    }
  }

  /**
   * Complete initialization: prefetch module/CSS + data.
   *
   *
   * @param context - The current context (must contain clipId in metadata)
   */
  async initialize(context: AgenticContext): Promise<void> {
    const clipId = context.metadata.clipId as string;
    if (clipId) {
      this.currentClipId = clipId;
    }

    window?.logger?.info('AIEditsAdapter: Starting complete initialization');

    await Promise.all([prefetchModuleAndStyles(), this.prefetchData(context)]);

    window?.logger?.info('AIEditsAdapter: Complete initialization finished');
  }
  /**
   * Send a user message to the AI Edits backend
   *
   * @param message - The user's message text
   * @param context - The current context (must contain clipId in metadata)
   * @param recipeId - Optional recipe ID if message originated from a recipe click
   * @param isQuickAction - Optional flag to indicate this is a quick action that bypasses clarification
   * @param isSuggestionOnly - Optional flag indicating if message is composed entirely of suggestions
   * @returns Promise resolving to the AI's response
   */
  async sendMessage(
    message: string,
    context: AgenticContext,
    recipeId?: string,
    isQuickAction?: boolean,
    isSuggestionOnly?: boolean
  ): Promise<ChatResponse> {
    if (!message.trim()) {
      throw new Error('Message cannot be empty');
    }

    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      throw new Error('clipId is required in context.metadata');
    }

    this.currentClipId = clipId;

    const shouldBypassClarification = isQuickAction || Boolean(recipeId) || Boolean(isSuggestionOnly);

    const clipData = currentClip.getSnapshot()[clipId];

    try {
      // Skip tracking for quick actions, as it is already tracked in handleHelpAction with more context
      if (!isQuickAction) {
        const isFirstMessage = !aiEditsStore.getSnapshot().hadInteraction;

        if (recipeId) {
          const recipes = getFromCache<Recipe[]>(CACHE_KEYS.recipes) || [];
          const recommendedIds = getFromCache<string[]>(CACHE_KEYS.recommendedRecipe(clipId)) || null;
          const recipe = recipes.find(r => r.id === recipeId);

          const isRecommended = recommendedIds?.[0] === recipeId;

          window.analytics?.track('ContentLabEvent', {
            name: 'AIEditsInteraction',
            interactionType: isRecommended ? 'RecommendedRecipe' : 'OtherRecipe',
            isFirstMessage: true,
            recipeId: recipeId,
            recipeName: recipe?.recipeName,
            recipeType: recipe?.recipeType,
            clipId,
            mediaType: clipData?.content?.media_type
          });
        } else if (isSuggestionOnly) {
          window.analytics?.track('ContentLabEvent', {
            name: 'AIEditsInteraction',
            interactionType: 'SuggestionInteraction',
            isFirstMessage: isFirstMessage,
            messageText: message,
            clipId,
            mediaType: clipData?.content?.media_type
          });
        } else {
          window.analytics?.track('ContentLabEvent', {
            name: 'AIEditsInteraction',
            interactionType: 'ManualChat',
            isFirstMessage: isFirstMessage,
            messageText: message,
            clipId,
            mediaType: clipData?.content?.media_type
          });
        }
      }

      aiEditsStore.setHadInteraction(true);

      const requestBody: any = {
        asset_id: clipId
      };

      requestBody.user_prompt = message;
      requestBody.effects = [];
      if (shouldBypassClarification) {
        requestBody.bypass_clarification = true;
      }

      const params = {
        body: requestBody
      };

      const response = await assetsAiEditsSendUserMessageCreate(params);

      if (!response) {
        throw new Error('No response from backend');
      }

      if (response.asset_id && this.currentClipId && response.asset_id !== this.currentClipId) {
        return {
          message: {
            content: undefined,
            type: 'ai'
          },
          chatSessionId: undefined
        };
      }

      const chatMessage: ChatMessage = {
        content: response.ai_message,
        type: 'ai',
        ...(Object.values(response.options || {}).length > 0 && {
          options: {
            type: 'actionable',
            buttonTexts: Object.values(response.options)
          }
        })
      };

      if (response.id) {
        setInCache(CACHE_KEYS.editId(clipId), response.id);
      }
      // Return response with metadata
      if (response.edits_started) {
        const processingStatus = mapAgentState(AGENT_STATES.PROCESSING, response.id);
        aiEditsStore.setAgentStatus(processingStatus);
      } else {
        const planningStatus = mapAgentState(AGENT_STATES.PLANNING);
        aiEditsStore.setAgentStatus(planningStatus);
      }
      return {
        message: chatMessage,
        chatSessionId: response.id
      };
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error sending message:', error);
      showErrorToast('Failed to send message. Please try again.');
      throw error;
    }
  }

  async handleHelpAction(helpAction: HelpAction, context: AgenticContext): Promise<ChatResponse> {
    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      throw new Error('clipId is required in context.metadata');
    }

    const clipData = currentClip.getSnapshot()[clipId];
    if (!clipData) {
      throw new Error('Clip data not found');
    }

    try {
      let recipePrompt: string;
      if (helpAction.id === 'optimize-linkedIn') {
        recipePrompt = await handleLinkedInOptimization(clipId, clipData);
      } else if (helpAction.id === 'optimize-youtube') {
        recipePrompt = await handleYouTubeOptimization(clipId, clipData);
      } else {
        return this.sendMessage(helpAction.prompt || helpAction.description, context);
      }

      const isFirstMessage = !aiEditsStore.getSnapshot().hadInteraction;
      window.analytics?.track('ContentLabEvent', {
        name: 'AIEditsInteraction',
        interactionType: 'QuickAction',
        isFirstMessage: isFirstMessage,
        platform: helpAction.id === 'optimize-linkedIn' ? 'LinkedIn' : 'YouTube',
        actionTitle: helpAction.title,
        clipId: clipId,
        mediaType: clipData.content?.media_type
      });

      aiEditsStore.setHadInteraction(true);

      return this.sendMessage(recipePrompt, context, undefined, true);
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error handling help action:', error);
      showErrorToast(`Failed to ${helpAction.title.toLowerCase()}`);
      throw error;
    }
  }

  /**
   * Apply an action (send as a message)
   *
   * In AI Edits, actions are implemented as messages.
   * When a user clicks an action button, we send that text as a message.
   *
   * @param action - The action text (button label) clicked by the user
   * @param context - The current context
   */
  async applyAction(action: string, context: AgenticContext): Promise<void> {
    try {
      await this.sendMessage(action, context);
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error applying action:', error);
      throw error;
    }
  }

  /**
   * Get the current agent status and apply edits if completed
   *
   * This method:
   * 1. Fetches the current AI Edits status
   * 2. Loads chat history if available
   * 3. Applies edits if status is Completed
   * 4. Returns the current agent state
   *
   * @param context - The current context (must contain clipId in metadata)
   * @param _chatSessionId - Optional chat session ID (not used in current implementation)
   * @returns Promise resolving to the current agent status
   */
  async getStatus(context: AgenticContext, _chatSessionId?: string): Promise<AgentStatus> {
    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      throw new Error('clipId is required in context.metadata');
    }

    this.currentClipId = clipId;

    try {
      const params = {
        body: {
          asset_id: clipId
        }
      };

      const data = await assetsAiEditsGetAiEditsCreate(params);

      if (data?.id) {
        setInCache(CACHE_KEYS.editId(clipId), data.id);
      }

      let status: AgentStatus;
      if (data?.status === 'Completed' && data?.ai_edits_config?.ai_edits) {
        const applied = await this.applyEditsInternal(clipId, data.ai_edits_config, data.id);
        status = applied
          ? mapAgentState(AGENT_STATES.APPLIED, data.id)
          : mapAgentState(AGENT_STATES.PROCESSING, data.id);
      } else if (data?.status) {
        status = mapAgentState(data.status, data.id);
      } else {
        status = mapAgentState(AGENT_STATES.IDLE, data?.id);
      }

      aiEditsStore.setAgentStatus(status);

      if (status.state !== 'Idle') {
        aiEditsStore.setHadInteraction(true);
      }
      return status;
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error fetching status:', error);
      const idleStatus = mapAgentState(AGENT_STATES.IDLE);
      aiEditsStore.setAgentStatus(idleStatus);
      return idleStatus;
    }
  }

  /**
   * Load chat history for the current clip
   *
   * Always fetches fresh data. For instant rendering, use getCachedHistory() instead.
   * This method implements stale-while-revalidate: returns fresh data and updates cache.
   *
   * @param context - The current context (must contain clipId in metadata)
   * @param chatSessionId - Optional chat session ID for filtering history
   * @returns Promise resolving to an array of chat messages
   */
  async getChatHistory(context: AgenticContext, chatSessionId?: string): Promise<ChatMessage[]> {
    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      throw new Error('clipId is required in context.metadata');
    }

    try {
      let editId = chatSessionId || getFromCache<string>(CACHE_KEYS.editId(clipId));

      if (!editId) {
        const statusData = await assetsAiEditsGetAiEditsCreate({
          body: { asset_id: clipId }
        });
        editId = statusData?.id;
        if (editId) {
          setInCache(CACHE_KEYS.editId(clipId), editId);
        }
      }

      if (editId) {
        const chatHistoryParams = {
          body: {
            ai_edits_id: editId
          }
        };

        const chatHistoryResponse = await assetsAiEditsGetChatHistoryCreate(chatHistoryParams);

        const chatHistory = chatHistoryResponse?.chat_history;
        if (Array.isArray(chatHistory)) {
          const messages = chatHistory.map(mapChatMessage);
          setInCache(CACHE_KEYS.chatHistory(clipId), messages);
          return messages;
        }
      }

      const emptyHistory: ChatMessage[] = [];
      setInCache(CACHE_KEYS.chatHistory(clipId), emptyHistory);
      return emptyHistory;
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error fetching chat history:', error);
      return [];
    }
  }

  async getRecipes(_context: AgenticContext): Promise<Recipe[]> {
    return getRecipesService(_context);
  }

  async getRecommendedRecipe(context: AgenticContext): Promise<string[] | null> {
    return getRecommendedRecipeService(context);
  }

  /**
   * Get help actions for the current context
   *
   * Help actions are static data computed on-demand, not cached.
   *
   * @param context - The current context (uses context.type to filter actions)
   * @returns Promise resolving to an array of help actions
   */
  async getHelpActions(context: AgenticContext): Promise<HelpAction[]> {
    try {
      return getHelpActionsByContext(context.type);
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error fetching help actions:', error);
      return [];
    }
  }

  /**
   * Get suggestions for the current context
   *
   * Suggestions are static data computed on-demand, not cached.
   *
   * @param context - The current context (uses context.type to filter suggestions)
   * @returns Promise resolving to an array of suggestion categories
   */
  async getSuggestions(context: AgenticContext): Promise<SuggestionCategory[]> {
    try {
      const contentStudioSuggestions = getSuggestionsByContext(context.type);
      return contentStudioSuggestions.map(mapSuggestionCategory);
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error fetching suggestions:', error);
      return [];
    }
  }

  // ==================== Synchronous Cache Access Methods ====================

  /**
   * Get cached chat history synchronously, if available.
   *
   * @param context - The current context (must contain clipId in metadata)
   * @returns Cached chat history, or null if not cached
   */
  getCachedHistory(context: AgenticContext): ChatMessage[] | null {
    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      return null;
    }
    return getFromCache<ChatMessage[]>(CACHE_KEYS.chatHistory(clipId));
  }

  /**
   * Get cached recipes synchronously, if available.
   *
   * @param _context - The current context (not used, recipes are global)
   * @returns Cached recipes, or null if not cached
   */
  getCachedRecipes(_context: AgenticContext): Recipe[] | null {
    return getFromCache<Recipe[]>(CACHE_KEYS.recipes);
  }

  /**
   * Get cached agent status synchronously from aiEditsStore.
   *
   * Status is stored in aiEditsStore for reactive UI updates.
   *
   * @param _context - The current context
   * @returns Current agent status from store, or null if Idle
   */
  getCachedStatus(_context: AgenticContext): AgentStatus | null {
    const status = aiEditsStore.getSnapshot().agentStatus;
    return status.state !== 'Idle' ? status : null;
  }

  /**
   * Get suggestions synchronously (computed on-demand, not cached).
   *
   * @param context - The current context (uses context.type to filter suggestions)
   * @returns Suggestions for the current context
   */
  getCachedSuggestions(context: AgenticContext): SuggestionCategory[] | null {
    try {
      const contentStudioSuggestions = getSuggestionsByContext(context.type);
      return contentStudioSuggestions.map(mapSuggestionCategory);
    } catch (error) {
      return null;
    }
  }

  /**
   * Get help actions synchronously (computed on-demand, not cached).
   *
   * @param context - The current context (uses context.type to filter actions)
   * @returns Help actions for the current context
   */
  getCachedHelpActions(context: AgenticContext): HelpAction[] | null {
    try {
      return getHelpActionsByContext(context.type);
    } catch (error) {
      return null;
    }
  }

  /**
   * Get cached recommended recipe IDs synchronously, if available.
   *
   * @param context - The current context (must contain clipId in metadata)
   * @returns Cached recommended recipe IDs array, or null if not cached or no recommended recipe
   */
  getCachedRecommendedRecipe(context: AgenticContext): string[] | null {
    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      return null;
    }
    return getFromCache<string[] | null>(CACHE_KEYS.recommendedRecipe(clipId)) ?? null;
  }

  /**
   * Subscribe to real-time streaming updates from the backend via Pusher.
   *
   * Uses singleton subscription management to prevent duplicate handlers:
   * - Checks if already subscribed to this clipId
   * - Cleans up any existing subscription before creating new one
   * - Ensures only ONE Pusher handler exists per clipId
   *
   * This method:
   * 1. Listens for AI_AGENT_UPDATE events on the Pusher channel
   * 2. Filters events by asset_id (clipId) to match current context
   * 3. Transforms AIAgentStreamingResponse to StreamingUpdate format
   * 4. Calls the callback with transformed updates
   * 5. Handles completion states (SUCCESS/FAILED) by applying edits
   * 6. Sends updates with status field for state changes (e.g., after SUCCESS/FAILED)
   *
   * @param context - The current context (must contain clipId in metadata)
   * @param callback - Function to call when streaming updates or status changes are received
   * @returns Unsubscribe function to clean up the subscription
   */
  subscribeToUpdates?(context: AgenticContext, callback: StreamingUpdateCallback): () => void {
    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      window?.logger?.error('AIEditsAdapter: clipId is required for streaming updates');
      return () => {};
    }

    if (!this.pusherChannel) {
      window?.logger?.warn('AIEditsAdapter: No Pusher channel available for streaming updates');
      return () => {};
    }

    const channel = this.pusherChannel;

    if (this.subscriptionManager.isSubscribed(clipId)) {
      window?.logger?.info('AIEditsAdapter: Already subscribed to clipId, cleaning up old subscription:', clipId);
      this.subscriptionManager.unsubscribe(clipId);
    }

    const handleEvent = (state: string, data: AIAgentStreamingResponse) => {
      if (state.startsWith('AI_AGENT_UPDATE')) {
        if (data.asset_id !== clipId) {
          window?.logger?.info('AIEditsAdapter: Ignoring event for different clip', {
            eventClipId: data.asset_id,
            currentClipId: clipId
          });
          return;
        }

        if (data.response === 'SUCCESS' || data.response === 'FAILED') {
          window?.logger?.info('AIEditsAdapter: AI processing completed with status:', data.response);

          if (data.response === 'FAILED') {
            window?.logger?.error('AIEditsAdapter: AI processing failed');
            showErrorToast('Something went wrong while applying AI edits! Please reload the page and try again.');

            // Send update with status change back to IDLE on failure
            const failedUpdate: StreamingUpdate = {
              agentName: data.agent_name || 'AI Editor',
              response: 'Processing failed',
              timestamp: new Date(),
              isThinking: false,
              assetId: clipId,
              status: {
                state: 'Idle'
              }
            };
            callback(failedUpdate);
            const idleStatus = mapAgentState(AGENT_STATES.IDLE);
            aiEditsStore.setAgentStatus(idleStatus);
          } else if (data.response === 'SUCCESS') {
            (async () => {
              try {
                window?.logger?.info('AIEditsAdapter: Auto-applying edits after SUCCESS event');

                const statusData = await assetsAiEditsGetAiEditsCreate({
                  body: { asset_id: clipId }
                });

                if (statusData?.ai_edits_config?.ai_edits) {
                  const applied = await this.applyEditsInternal(clipId, statusData.ai_edits_config, statusData.id);

                  if (applied) {
                    window?.logger?.info('AIEditsAdapter: Successfully auto-applied edits from streaming event');

                    const appliedStatus = mapAgentState(AGENT_STATES.APPLIED, statusData.id);
                    aiEditsStore.setAgentStatus(appliedStatus);
                    const statusUpdate: StreamingUpdate = {
                      agentName: data.agent_name || 'AI Editor',
                      response: 'Edits applied successfully',
                      timestamp: new Date(),
                      isThinking: false,
                      assetId: clipId,
                      status: appliedStatus
                    };
                    callback(statusUpdate);
                  } else {
                    window?.logger?.warn('AIEditsAdapter: Edits already being applied by another process');
                  }
                } else {
                  window?.logger?.warn('AIEditsAdapter: No edits found in SUCCESS response');
                  const completedStatus = mapAgentState(AGENT_STATES.COMPLETED, statusData.id);
                  aiEditsStore.setAgentStatus(completedStatus);
                }
              } catch (error) {
                window?.logger?.error('AIEditsAdapter: Failed to auto-apply edits on SUCCESS:', error);
                showErrorToast('Something went wrong while applying AI edits! Please reload the page and try again.');
                const idleStatus = mapAgentState(AGENT_STATES.IDLE);
                aiEditsStore.setAgentStatus(idleStatus);
              }
            })();
          }
          return;
        }

        const streamingUpdate: StreamingUpdate = {
          agentName: data.agent_name,
          response: data.response,
          timestamp: new Date(data.timestamp),
          isThinking: data.is_thinking,
          assetId: data.asset_id
        };

        callback(streamingUpdate);
      }
    };

    channel.bind_global(handleEvent);

    const unsubscribe = () => {
      channel.unbind_global(handleEvent);
    };

    this.subscriptionManager.subscribe(clipId, unsubscribe);

    return () => {
      this.subscriptionManager.unsubscribe(clipId);
    };
  }

  /**
   * Internal method to apply AI edits to a clip
   *
   * This method:
   * 1. Waits for clip data to be available
   * 2. Acquires a processing lock
   * 3. Calls processAIEditsResponse to apply edits
   * 4. Shows confetti on success
   * 5. Updates backend status to Applied
   * 6. Shows success toast
   *
   * @param clipId - The clip ID
   * @param aiEditsConfig - The AI edits configuration from the backend
   * @param chatSessionId - The chat session ID
   */
  private async applyEditsInternal(clipId: string, aiEditsConfig: any, chatSessionId?: string): Promise<boolean> {
    const allClips = currentClip.getSnapshot();
    const clipData = allClips[clipId];

    // If clip data not available yet, wait and retry
    if (!clipData) {
      window?.logger?.warn('AIEditsAdapter: No clip data found for clipId:', clipId);
      return new Promise<boolean>(resolve => {
        setTimeout(async () => {
          const result = await this.applyEditsInternal(clipId, aiEditsConfig, chatSessionId);
          resolve(result);
        }, 1000);
      });
    }

    if (!acquireLock(clipId)) {
      window?.logger?.warn('AIEditsAdapter: Lock already held for clipId, skipping:', clipId);
      return false;
    }

    try {
      await processAIEditsResponse(aiEditsConfig, clipId, clipData);
      triggerConfetti();

      if (chatSessionId && aiEditsStore.getSnapshot().agentStatus.state !== 'Applied') {
        try {
          await assetsAiEditsUpdateAiEditsStatusCreate({
            body: {
              id: chatSessionId,
              status: 'Applied'
            }
          });
        } catch (error) {
          window?.logger?.error('AIEditsAdapter: Failed to update AI edits status:', error);
        }
      }

      showSuccessToast(`AI edits applied successfully to ${clipData.title}`);
      return true;
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Failed to apply edits:', error);
      showErrorToast('Something went wrong while applying AI edits! Please reload the page and try again.');
      throw error;
    } finally {
      releaseLock(clipId);
    }
  }

  /**
   * Generate a new recipe from the current agent's successful edits.
   *
   * This method fetches recipe data from the backend based on the current chatSessionId.
   * It requires chatSessionId to be present in context.metadata.
   *
   * @param context - The current context (must contain chatSessionId in metadata)
   * @returns Promise resolving to the generated recipe data
   * @throws Error if chatSessionId is missing or recipe generation fails
   */
  async generateRecipe(context: AgenticContext): Promise<RecipeData> {
    return generateRecipeService(context);
  }

  async updateRecipe(recipeId: string, data: RecipeData): Promise<void> {
    return updateRecipeService(recipeId, data);
  }

  async deleteRecipe(recipeId: string): Promise<void> {
    return deleteRecipeService(recipeId);
  }

  async getRecipeTags(): Promise<string[]> {
    return getRecipeTagsService();
  }

  async clearChatHistory(context: AgenticContext): Promise<ChatMessage[]> {
    const clipId = context.metadata.clipId as string;
    if (!clipId) {
      throw new Error('clipId is required in context.metadata');
    }

    try {
      let editId = getFromCache<string>(CACHE_KEYS.editId(clipId));

      // Only fetch if no cached editId
      if (!editId) {
        const statusData = await assetsAiEditsGetAiEditsCreate({
          body: { asset_id: clipId }
        });
        editId = statusData?.id;
        if (editId) {
          setInCache(CACHE_KEYS.editId(clipId), editId);
        }
      }

      if (!editId) {
        window?.logger?.warn('AIEditsAdapter: No editId found, returning empty chat history');
        setInCache(CACHE_KEYS.chatHistory(clipId), []);
        aiEditsStore.setHadInteraction(false);
        return [];
      }

      const clearParams = {
        queryParams: {
          ai_edits_id: editId
        }
      };

      const clearResponse = await assetsAiEditsClearChatHistoryCreate(clearParams);

      const chatHistory = clearResponse?.chat_history;
      if (Array.isArray(chatHistory)) {
        const messages = chatHistory.map(mapChatMessage);
        setInCache(CACHE_KEYS.chatHistory(clipId), messages);
        window?.logger?.info('AIEditsAdapter: Chat history cleared successfully');
        aiEditsStore.setHadInteraction(false);
        return messages;
      }

      const emptyHistory: ChatMessage[] = [];
      setInCache(CACHE_KEYS.chatHistory(clipId), emptyHistory);
      aiEditsStore.setHadInteraction(false);
      return emptyHistory;
    } catch (error) {
      window?.logger?.error('AIEditsAdapter: Error clearing chat history:', error);
      throw new Error('Failed to clear chat history. Please try again.');
    }
  }
}
